import React, { Fragment, useEffect, useState, useMemo } from 'react'
import { MoneyFormat } from '../../../../widgets/NumberFormat';
import { useSelector } from 'react-redux';

const YearlyBudgetListView = () => {
    const { account_trial_balance_rpt_list: data } = useSelector((state) => state.accounts);
    const [totalEstimates, setTotalEstimates] = useState({ income: 0, expense: 0 });
    const [totalActuals, setTotalActuals] = useState({ income: 0, expense: 0 });
    const [totalDifferences, setTotalDifferences] = useState({ income: 0, expense: 0 });

    const { incomeItems, expenseItems } = useMemo(() => {
        const income = [];
        const expense = [];

        data?.results?.forEach(item => {
            if (item.glid === 150 || item.glid === 130) {
                expense.push(item);
            } else {
                income.push(item);
            }
        });

        return { incomeItems: income, expenseItems: expense };
    }, [data]);

    useEffect(() => {
        if (!data?.results) return;

        let incomeEstimate = 0, incomeActual = 0, incomeDiff = 0;
        let expenseEstimate = 0, expenseActual = 0, expenseDiff = 0;

        data.results.forEach(item => {
            if (item.glid === 150 || item.glid === 130) {
                expenseEstimate += item.estimated_amount || 0;
                expenseActual += item.real_amount || 0;
                expenseDiff += item.difference_amount || 0;
            } else {
                incomeEstimate += item.estimated_amount || 0;
                incomeActual += item.real_amount || 0;
                incomeDiff += item.difference_amount || 0;
            }
        });

        setTotalEstimates({ income: incomeEstimate, expense: expenseEstimate });
        setTotalActuals({ income: incomeActual, expense: expenseActual });
        setTotalDifferences({ income: incomeDiff, expense: expenseDiff });
    }, [data]);

    const renderTableRows = (items, isIncome) => {
        const maxRows = Math.max(incomeItems?.length, expenseItems.length);

        return Array.from({ length: maxRows }).map((_, index) => {
            const item = items[index];

            if (!item) {
                return (
                    <tr key={`${isIncome ? 'income' : 'expense'}-empty-${index}`}
                        className="bg-white dark:bg-slate800 border-b dark:border-slate700">
                        <td align='center' className="px-3 py-1 border dark:border-slate600"></td>
                        <td align='center' className="px-3 py-1 border dark:border-slate600"></td>
                        <td align='right' className="px-3 py-1 border dark:border-slate600"></td>
                        <td align='right' className="px-3 py-1 border dark:border-slate600"></td>
                        <td align='right' className="px-3 py-1 border dark:border-slate600"></td>
                    </tr>
                );
            }

            const diffClass = item?.difference_amount >= 0
                ? 'text-green-600 dark:text-green-400'
                : 'text-red-600 dark:text-red-400';

            return (
                <tr key={item?.sglsn}
                    className="bg-white font-public-sans dark:bg-slate800 border-b dark:border-slate700 hover:bg-gray-50 dark:hover:bg-slate-700">
                    <td align='center' className="px-1 w-10 py-1 border dark:border-slate600">
                        {index + 1}
                    </td>
                    <td className="px-3 py-1 border dark:border-slate600">
                        <div>
                            <div className="font-normal">{item?.sub_ledg_name}</div>
                        </div>
                    </td>
                    <td align='right' className="px-3 py-1 border dark:border-slate600">
                        {MoneyFormat(item?.estimated_amount)}
                    </td>
                    <td align='right' className="px-3 py-1 border dark:border-slate600">
                        {MoneyFormat(item?.real_amount)}
                    </td>
                    <td align='right' className={`px-3 py-1 border dark:border-slate600 ${diffClass}`}>
                        {MoneyFormat(item?.difference_amount)}
                    </td>
                </tr>
            );
        });
    };

    return (
        <Fragment>
            <div className='bg-white rounded-lg font-public-sans shadow-sm border border-gray-200 dark:border-slate-800 dark:bg-slate-900 p-4'>
                <div className="relative overflow-x-auto overflow-y-auto" style={{ maxHeight: '70vh' }}>
                    <table className="w-full text-xs text-left text-gray-700 dark:text-gray-300">
                        <thead className="text-xs bg-gray-100 dark:bg-slate-800 sticky top-0">
                            <tr>
                                <th colSpan={5} className="px-3 py-1 border-r dark:border-slate-700 text-center bg-blue-50 dark:bg-blue-900/20">
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-green-500"></div>
                                        <span>INCOME</span>
                                    </div>
                                </th>

                                <th colSpan={5} className="px-3 py-1 text-center bg-red-50 dark:bg-red-900/20">
                                    <div className="flex items-center justify-center gap-2">
                                        <div className="w-3 h-3 rounded-full bg-red-500"></div>
                                        <span>EXPENSE</span>
                                    </div>
                                </th>
                            </tr>
                            <tr>
                                <th className="px-1 py-1 w-10 border dark:border-slate-700 bg-gray-50 dark:bg-slate-800">S.N.</th>
                                <th className="px-3 py-1 border dark:border-slate-700 bg-gray-50 dark:bg-slate-800">Ledger Name</th>
                                <th className="px-3 py-1 border dark:border-slate-700 bg-gray-50 dark:bg-slate-800">Estimated</th>
                                <th className="px-3 py-1 border dark:border-slate-700 bg-gray-50 dark:bg-slate-800">Actual</th>
                                <th className="px-3 py-1 border-r dark:border-slate-700 bg-gray-50 dark:bg-slate-800">Difference</th>

                                <th className="px-1 py-1 w-10 border dark:border-slate-700 bg-gray-50 dark:bg-slate-800">S.N.</th>
                                <th className="px-3 py-1 border dark:border-slate-700 bg-gray-50 dark:bg-slate-800">Ledger Name</th>
                                <th className="px-3 py-1 border dark:border-slate-700 bg-gray-50 dark:bg-slate-800">Estimated</th>
                                <th className="px-3 py-1 border dark:border-slate-700 bg-gray-50 dark:bg-slate-800">Actual</th>
                                <th className="px-3 py-1 border dark:border-slate-700 bg-gray-50 dark:bg-slate-800">Difference</th>
                            </tr>
                        </thead>

                        <tbody>
                            {renderTableRows(incomeItems, true).map((incomeRow, index) => {
                                const expenseRow = renderTableRows(expenseItems, false)[index];
                                return (
                                    <React.Fragment key={index}>
                                        {React.cloneElement(incomeRow, {
                                            children: [
                                                ...incomeRow.props.children,
                                                ...expenseRow.props.children
                                            ]
                                        })}
                                    </React.Fragment>
                                );
                            })}

                            <tr className="font-bold bg-gray-100 dark:bg-slate-800">
                                <td colSpan={2} className="px-3 py-1 border dark:border-slate-700 text-right">
                                    Total Income:
                                </td>
                                <td className="px-3 py-1 border dark:border-slate-700 text-right">
                                    {MoneyFormat(totalEstimates.income)}
                                </td>
                                <td className="px-3 py-1 border dark:border-slate-700 text-right">
                                    {MoneyFormat(totalActuals.income)}
                                </td>
                                <td className={`px-3 py-1 border-r dark:border-slate-700 text-right ${totalDifferences.income >= 0
                                        ? 'text-green-600 dark:text-green-400'
                                        : 'text-red-600 dark:text-red-400'
                                    }`}>
                                    {MoneyFormat(totalDifferences.income)}
                                </td>

                                <td colSpan={2} className="px-3 py-1 border dark:border-slate-700 text-right">
                                    Total Expense:
                                </td>
                                <td className="px-3 py-1 border dark:border-slate-700 text-right">
                                    {MoneyFormat(totalEstimates.expense)}
                                </td>
                                <td className="px-3 py-1 border dark:border-slate-700 text-right">
                                    {MoneyFormat(totalActuals.expense)}
                                </td>
                                <td className={`px-3 py-1 border dark:border-slate-700 text-right`}>
                                    {MoneyFormat(totalDifferences.expense)}
                                </td>
                            </tr>

                            <tr className="font-bold bg-gray-50 dark:bg-blue-900/20">
                                <td colSpan={8} className="px-3 py-1 border-r dark:border-slate-700 text-end">
                                    Net Result:
                                </td>
                                <td colSpan={2} className={`px-3 py-1 text-end text-xs`}>
                                    {MoneyFormat(totalActuals.income - totalActuals.expense)}
                                </td>
                            </tr>
                            
                        </tbody>
                    </table>
                </div>
            </div>
        </Fragment>
    );
};

export default YearlyBudgetListView;